export type ThemePreference = 'light' | 'dark' | 'system'

export interface UserSettingsDTO {
  concurrency: number
  theme: ThemePreference
}

/** Instance-wide settings. Only admins may read or write these. */
export interface AppSettingsDTO {
  claudeCliPath: string
  allowRegistration: boolean
  sshDriver: 'ssh2' | 'system'
  /** Users allowed to generate simultaneously; the rest queue for a slot. */
  maxConcurrentUsers: number
  /**
   * Shared pool of in-flight images across everyone generating. One user alone
   * takes the whole pool; it is split round-robin only when others are active.
   */
  maxConcurrentImages: number
}

/**
 * Estimated RAM for the generation pool. `maxConcurrentImages` is the only input
 * because each in-flight image is one `claude` subprocess — the number of *users*
 * does not change the subprocess count.
 */
export function estimatedPoolRamMb(settings: Pick<AppSettingsDTO, 'maxConcurrentImages'>): number {
  return settings.maxConcurrentImages * CLAUDE_SUBPROCESS_RSS_MB
}

/** Roughly how much RAM those subprocesses need, from a measured ~392 MB each. */
export const CLAUDE_SUBPROCESS_RSS_MB = 400

/** Health of the host-level dependencies this instance shares between all users. */
export interface SystemStatusDTO {
  claude: {
    available: boolean
    version: string | null
    path: string
    error: string | null
  }
  ssh: {
    driver: 'ssh2' | 'system'
    /** Only meaningful for the `system` driver. */
    binaryAvailable: boolean
  }
  queue: {
    /** The shared in-flight image pool — equals `maxConcurrentImages`. */
    globalConcurrency: number
    running: number
    queued: number
    /** Slots currently held by a generating user, out of `maxConcurrentUsers`. */
    activeUsers: number
    maxConcurrentUsers: number
    /** Jobs waiting for a free slot. */
    waitingUsers: number
  }
  version: string
}

// SessionLifetimeDTO lives in ./auth — it is part of the session contract.
